/**
 * URL pattern matching for tracking rules: ONE implementation, shared by the
 * tracking script (url-matching.ts) and the server-side matcher
 * (WebsiteRepo). It existed as two hand-kept copies before, which is the
 * exact two-copies-drift failure this codebase has been bitten by.
 *
 * Semantics:
 * - A pattern that is just a path ("/onboarding") matches that path on EVERY
 *   domain the script runs on. This has always been the behavior and is what
 *   makes app.customer.com work without any configuration.
 * - A pattern that starts with a host ("app.customer.com/onboarding") only
 *   matches on exactly that host, path rules unchanged. This is the rule-level
 *   subdomain scoping the wizard's domain chip writes.
 * - A pattern WITHOUT a query ("/onboarding") ignores the query of the URL,
 *   as it always did: the query and the hash are stripped from both sides.
 * - A pattern WITH a query ("/onboarding?step=welcome") matches only when the
 *   URL carries every parameter the pattern names, with the same value. Other
 *   parameters on the URL are ignored, order is ignored, a "*" value means
 *   "present with any value". Before 2 September 2026 the query was stripped
 *   from the pattern too, so a rule on ?step=welcome fired on every
 *   onboarding step and again on every return visit: five registration
 *   events for one person, with nothing on screen to say why.
 * - The hash never takes part.
 */

/** Max glob pattern length, guarding regex construction. Lives here (not in
 *  the script's constants.ts) because this module is on the backend build's
 *  shared-files whitelist and must not drag script-only modules along. */
export const MAX_GLOB_PATTERN_LENGTH = 500;

export interface SplitPattern {
  /** Lowercased hostname the pattern is scoped to, or null for every domain */
  readonly host: string | null;
  /** The path part of the pattern, always starting with "/" (or the raw
   *  pattern when it never looked like a URL at all) */
  readonly path: string;
}

/**
 * Split a rule pattern into an optional host and a path. A pattern carries a
 * host when it starts with a scheme ("https://app.x.nl/p") or when its first
 * segment contains a dot before the first slash ("app.x.nl/p"). A leading
 * slash or a plain path fragment stays host-less. Wildcards in the host are
 * not supported: a "*" in the first segment keeps the whole pattern a path.
 */
export function splitPattern(pattern: string): SplitPattern {
  const trimmed = pattern.trim();
  if (trimmed === "" || trimmed.startsWith("/")) {
    return { host: null, path: trimmed };
  }
  let candidate = trimmed;
  const schemeMatch = candidate.match(/^https?:\/\//i);
  if (schemeMatch) {
    candidate = candidate.slice(schemeMatch[0].length);
  }
  const slashIndex = candidate.indexOf("/");
  let firstSegment: string;
  if (slashIndex === -1) {
    firstSegment = candidate;
  } else {
    firstSegment = candidate.slice(0, slashIndex);
  }
  const looksLikeHost = firstSegment.indexOf(".") !== -1 && firstSegment.indexOf("*") === -1;
  if (!schemeMatch && !looksLikeHost) {
    return { host: null, path: trimmed };
  }
  const host = firstSegment.toLowerCase();
  let path: string;
  if (slashIndex === -1) {
    path = "/";
  } else {
    path = candidate.slice(slashIndex);
  }
  return { host, path };
}

/** The pattern's own query parameters, keyed lowercase, or null when the
 *  pattern carries no "?". A "*" value means "present with any value". */
export function patternQuery(path: string): ReadonlyArray<readonly [string, string]> | null {
  const questionIndex = path.indexOf("?");
  if (questionIndex === -1) return null;
  const query = path.slice(questionIndex + 1).split("#")[0]!;
  const pairs: Array<readonly [string, string]> = [];
  const parts = query.split("&");
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;
    if (part === "") continue;
    const equalsIndex = part.indexOf("=");
    if (equalsIndex === -1) {
      pairs.push([decodeQueryPart(part).toLowerCase(), "*"]);
    } else {
      pairs.push([
        decodeQueryPart(part.slice(0, equalsIndex)).toLowerCase(),
        decodeQueryPart(part.slice(equalsIndex + 1)),
      ]);
    }
  }
  if (pairs.length === 0) return null;
  return pairs;
}

/** URL-decode one query token; a malformed escape is kept as typed. */
function decodeQueryPart(part: string): string {
  try {
    return decodeURIComponent(part.replace(/\+/g, " "));
  } catch {
    return part;
  }
}

/** The URL's query as a lowercase-keyed map; null when the URL does not parse. */
function urlQuery(url: string): Map<string, string> | null {
  try {
    const parsedUrl = new URL(url);
    const result = new Map<string, string>();
    parsedUrl.searchParams.forEach((value, key) => {
      const lowerKey = key.toLowerCase();
      // First value wins, like the browser's own get(): a repeated key is a
      // malformed link, not two answers.
      if (!result.has(lowerKey)) result.set(lowerKey, value);
    });
    return result;
  } catch {
    return null;
  }
}

/**
 * Every parameter the pattern names must be on the URL with the same value.
 * Values compare case-insensitively: "?step=Welcome" and "?step=welcome" are
 * one page to a visitor, and a rule that treated them as two would silently
 * miss half of them.
 */
export function matchQuery(url: string, patternPath: string): boolean {
  const required = patternQuery(patternPath);
  if (required === null) return true;
  const present = urlQuery(url);
  if (present === null) return false;
  for (let i = 0; i < required.length; i++) {
    const [key, wanted] = required[i]!;
    const actual = present.get(key);
    if (actual === undefined) return false;
    if (wanted === "*") continue;
    if (actual.toLowerCase() !== wanted.toLowerCase()) return false;
  }
  return true;
}

/** Strip query, hash, trailing slashes, lowercase the path. */
export function normalizeUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    parsedUrl.hash = "";
    parsedUrl.search = "";
    const normalizedPath = parsedUrl.pathname.replace(/\/+$/, "") || "/";
    return normalizedPath.toLowerCase();
  } catch {
    return url
      .toLowerCase()
      .split("?")[0]!
      .split("#")[0]!;
  }
}

/** Lowercased hostname of a URL, or null when it does not parse. */
function hostnameOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/** Convert a glob pattern to a safe regex. `*` matches any characters except `/`. */
function globToRegex(pattern: string): RegExp {
  const escaped = pattern
    .replace(/\*{2,}/g, "*")
    .split("*")
    .map((s) => s.replace(/[.+?^${}()|[\]\\]/g, "\\$&"))
    .join("[^/]*");
  return new RegExp("^" + escaped + "$");
}

/** Check if a URL matches a rule's pattern. */
export function matchUrl(
  url: string,
  pattern: string | undefined,
  matchType: string | undefined,
): boolean {
  if (!pattern) return false;
  const split = splitPattern(pattern);
  if (split.host !== null) {
    const hostname = hostnameOf(url);
    if (hostname !== split.host) return false;
  }
  if (!matchQuery(url, split.path)) return false;
  const normalizedUrl = normalizeUrl(url);
  const normalizedPattern = normalizeUrl(split.path);
  switch (matchType) {
    case "exact":
      return normalizedUrl === normalizedPattern;
    case "starts_with":
      return normalizedUrl.startsWith(normalizedPattern);
    case "contains":
      return normalizedUrl.includes(normalizedPattern);
    case "wildcard":
      if (pattern.length > MAX_GLOB_PATTERN_LENGTH) return false;
      return globToRegex(normalizeUrl(split.path)).test(normalizedUrl);
    default:
      return false;
  }
}

/** Ceiling on the excluded-page list, shared by the API schema and the UI. */
export const MAX_EXCLUDED_PAGES = 50;

const TRAILING_WILDCARDS = /\*+$/;

/**
 * Whether one excluded-page pattern covers a URL.
 *
 * "/chat" covers /chat and everything below it (/chat/123), but not /chats:
 * a path segment is a page boundary, and a customer who wrote "/chat" did not
 * mean "/chatbot-review". A trailing "*" ("/chat*") drops that boundary and
 * covers every path starting with "/chat". A "*" anywhere else is the rules'
 * own wildcard. A host in front ("app.example.com/chat") scopes the pattern
 * to that host, exactly as in a tracking rule.
 */
function excludedPatternCovers(url: string, pattern: string): boolean {
  const split = splitPattern(pattern);
  if (split.path === "") return false;
  if (split.host !== null && hostnameOf(url) !== split.host) return false;
  let path = split.path;
  if (!path.startsWith("/")) path = "/" + path;
  const withoutTrailing = path.replace(TRAILING_WILDCARDS, "");
  if (withoutTrailing.indexOf("*") !== -1) return matchUrl(url, pattern, "wildcard");
  if (!matchQuery(url, withoutTrailing)) return false;
  const normalizedUrl = normalizeUrl(url);
  const normalizedPattern = normalizeUrl(withoutTrailing);
  if (withoutTrailing !== path) return normalizedUrl.startsWith(normalizedPattern);
  if (normalizedPattern === "/") return true;
  return normalizedUrl === normalizedPattern || normalizedUrl.startsWith(normalizedPattern + "/");
}

/**
 * Pages the customer told us never to measure. Nothing leaves for them: no
 * event, no rule, no browser pixel. The script checks this before it sends and
 * the server checks it again on arrival, so a cached older script cannot leak
 * a page either.
 */
export function isExcludedPage(url: string, patterns: ReadonlyArray<string> | null | undefined): boolean {
  if (!patterns || patterns.length === 0) return false;
  for (let i = 0; i < patterns.length; i++) {
    if (excludedPatternCovers(url, patterns[i]!)) return true;
  }
  return false;
}
